import React from 'react';
import classnames from 'classnames';

import { ensureCurrencyIsPresent } from './util';
import StripeChargeModalIconRenderWrapper, {
  DesktopTooltipProps,
  IconProps,
  ModalProps,
  TooltipProps
} from './StripeChargeModalIconRenderWrapper';

interface StripeChargeModalPaymentSummaryLineItemV2Props {
  className?: string | null;
  amountInDollars: string;
  description: string;
  longDescription?: React.ReactNode | null;
  discountedPriceInDollars?: string | null;
  iconProps?: IconProps | null;
  itemType: string;
  tooltipProps?: TooltipProps | null;
  disableInteractions?: boolean;
  modalProps?: ModalProps | null;
  desktopTooltipProps?: DesktopTooltipProps | null;
  // shown in place of the amount when there isn't one yet, e.g. "TBD"
  amountText?: string | null;
  // the new checkout's tighter, regular-weight rows
  compact?: boolean;
  // a pill after the label, e.g. a deposit's "Non-refundable"
  badge?: string | null;
}

export default function StripeChargeModalPaymentSummaryLineItemV2({
  className,
  amountInDollars,
  description,
  disableInteractions = false,
  discountedPriceInDollars = null,
  iconProps = null,
  itemType,
  longDescription = null,
  modalProps = null,
  tooltipProps = null,
  desktopTooltipProps = null,
  amountText = null,
  compact = false,
  badge = null
}: StripeChargeModalPaymentSummaryLineItemV2Props) {
  const amountWithCurrencySign =
    amountText ?? ensureCurrencyIsPresent(amountInDollars);
  const showIcon = iconProps != null && !disableInteractions;

  return (
    <>
      <div
        className={classnames(
          'StripeChargeModalPaymentSummaryLineItem flex',
          compact ? 'font-16 secondary' : 'font-14 md:font-16 pv6',
          className
        )}
      >
        <div className="flex-auto">
          <div
            className={classnames('inline-flex items-center', {
              'gap-4': !compact,
              'gap-8': compact
            })}
          >
            <span className={classnames({ 'fw-medium': !compact })}>
              {description}
            </span>
            {showIcon && (
              <StripeChargeModalIconRenderWrapper
                iconProps={
                  compact
                    ? { ...iconProps, width: '16px', height: '16px' }
                    : iconProps
                }
                tooltipProps={tooltipProps}
                modalProps={modalProps}
                desktopTooltipProps={desktopTooltipProps}
              />
            )}
            {badge && (
              <span className="StripeChargeModalPaymentSummaryLineItem__badge font-12 gray-700 bg-gray-200 ba b--gray-300 br-pill">
                {badge}
              </span>
            )}
          </div>
          {longDescription && (
            <p className="StripeChargeModalPaymentSummaryLineItem__description font-12 sm:font-16 secondary w-90-md">
              {longDescription}
            </p>
          )}
        </div>
        <p>
          {discountedPriceInDollars !== null && (
            <>
              <span className="strike secondary">
                {ensureCurrencyIsPresent(discountedPriceInDollars)}
              </span>
              &nbsp;&nbsp;
            </>
          )}
          <span
            className={classnames(
              'StripeChargeModalPaymentSummaryLineItem__amount',
              { 'fw-medium': !compact }
            )}
            data-test-id={`StripeChargeModalPaymentSummaryLineItem-${itemType}-amount`}
          >
            {amountWithCurrencySign}
          </span>
        </p>
      </div>
    </>
  );
}
